import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useRoomingList, useRoomingListDownload } from '@/lib/documents/use-documents';
import type { RoomingList } from '@/lib/documents/types';
import { DocumentSheetDialog, DocumentStamp, ExerciseBanner, UnknownHint } from './document-sheet-dialog';
import { fmtDate, fmtDateTime } from '@/lib/documents/format';

// The ROOMING LIST a hotel receives (#809, SKY PoC B6): one row per allocation
// party (a family across two PNRs is ONE row), the guests with their condition,
// the stay window and what the front desk must know (infant, assistance, minor's
// responsible adult). Printable A4 via the shared shell; CSV for the hotel's PMS.
//
// `exerciseLabel` is the hook for the drill watermark (#812 wires
// `NEXA_EXERCISE_MODE`); it defaults to undefined and nothing is rendered.

export function RoomingListSheet({ list, exerciseLabel }: { list: RoomingList; exerciseLabel?: string | undefined }) {
  const { t, i18n } = useTranslation('disruptions');
  const lang = i18n.language;
  const label = exerciseLabel ?? list.header.exerciseLabel ?? undefined;
  return (
    <article className="mx-auto max-w-3xl bg-white p-8 text-black shadow-sm print:p-0 print:shadow-none" data-print-sheet data-print-format="a4" data-testid="rooming-list-sheet">
      <ExerciseBanner exerciseLabel={label} />
      <header className="border-b-2 border-black pb-3">
        <p className="text-xs uppercase tracking-[0.3em] text-neutral-500">{t('documents.rooming.title')}</p>
        <h1 className="mt-1 text-2xl font-bold" data-testid="rooming-list-hotel">
          {list.hotel.name}
          {list.hotel.airport ? <span className="ml-2 text-base font-normal text-neutral-500">{list.hotel.airport}</span> : null}
        </h1>
        <dl className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-sm sm:grid-cols-4">
          <Field label={t('documents.rooming.flight')} value={list.header.disruption.flight ?? '—'} />
          <Field label={t('documents.rooming.disruption')} value={list.header.disruption.departureAt ? fmtDateTime(list.header.disruption.departureAt, lang) : '—'} />
          <Field label={t('documents.rooming.vendor')} value={list.hotel.vendor ?? '—'} />
          <Field label={t('documents.rooming.hotel')} value={list.hotel.hotelUrn ?? list.hotel.hotelRef} mono />
        </dl>
      </header>

      <UnknownHint known={list.known} />

      {list.rooms.length === 0 ? (
        <p className="mt-6 text-sm text-neutral-600" data-testid="rooming-list-empty">
          {t('documents.rooming.empty')}
        </p>
      ) : (
        <table className="mt-4 w-full border-collapse text-sm" data-testid="rooming-list-table">
          <thead>
            <tr className="border-b border-black text-left text-xs uppercase tracking-wide text-neutral-600">
              <th className="py-1 pr-2">{t('documents.rooming.col.room')}</th>
              <th className="py-1 pr-2">{t('documents.rooming.col.pnr')}</th>
              <th className="py-1 pr-2">{t('documents.rooming.col.passenger')}</th>
              <th className="py-1 pr-2">{t('documents.rooming.col.condition')}</th>
              <th className="py-1 pr-2">{t('documents.rooming.col.stay')}</th>
              <th className="py-1">{t('documents.rooming.col.notes')}</th>
            </tr>
          </thead>
          <tbody>
            {list.rooms.map((room) =>
              room.passengers.map((p, i) => (
                <tr key={p.paxUrn} className={i === 0 ? 'border-t border-neutral-400' : ''} data-testid="rooming-list-row" data-room={room.room}>
                  {i === 0 ? (
                    <td className="py-1 pr-2 align-top font-bold" rowSpan={room.passengers.length} data-testid="rooming-list-room">
                      {room.room}
                      {room.roomsReserved != null && room.roomsReserved > 1 ? (
                        <span className="block text-[10px] font-normal text-neutral-500">{t('documents.rooming.roomsReserved', { count: room.roomsReserved })}</span>
                      ) : null}
                    </td>
                  ) : null}
                  <td className="py-1 pr-2 align-top font-mono text-xs">{p.pnr}</td>
                  <td className="py-1 pr-2 align-top">{p.name}</td>
                  <td className="py-1 pr-2 align-top">{t(`documents.condition.${p.condition}`)}</td>
                  {i === 0 ? (
                    <td className="py-1 pr-2 align-top text-xs" rowSpan={room.passengers.length} data-testid="rooming-list-stay">
                      <span className="block font-medium">{room.nights != null ? t('documents.rooming.nights', { count: room.nights }) : '—'}</span>
                      <span className="block text-neutral-600">
                        {t('documents.rooming.checkIn')} {fmtDate(room.checkIn, lang)}
                      </span>
                      <span className="block text-neutral-600">
                        {t('documents.rooming.checkOut')} {fmtDate(room.checkOut, lang)}
                      </span>
                    </td>
                  ) : null}
                  <td className="py-1 align-top text-xs">
                    {p.notes.length > 0 ? <span className="block">{p.notes.join(' · ')}</span> : null}
                    {i === 0 && room.observations.length > 0 ? (
                      <span className="block font-medium text-neutral-800" data-testid="rooming-list-observations">
                        {room.observations.join(' · ')}
                      </span>
                    ) : null}
                  </td>
                </tr>
              )),
            )}
          </tbody>
        </table>
      )}

      <p className="mt-4 text-sm font-medium" data-testid="rooming-list-totals">
        {t('documents.rooming.totals', {
          rooms: list.totals.rooms,
          pnrs: list.totals.pnrs,
          passengers: list.totals.passengers,
          adults: list.totals.adults,
          minors: list.totals.minors,
          infants: list.totals.infants,
        })}
      </p>

      <DocumentStamp generatedAt={list.header.generatedAt} generatedBy={list.header.generatedBy} exerciseLabel={label} />
    </article>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="min-w-0">
      <dt className="text-[10px] uppercase tracking-wide text-neutral-500">{label}</dt>
      <dd className={mono ? 'truncate font-mono text-xs' : 'truncate'}>{value}</dd>
    </div>
  );
}

/** "Descargar rooming list" — one per hotel card; opens the printable sheet with the CSV beside it. */
export function RoomingListButton({
  disruptionUrn,
  hotelUrn,
  hotelName,
  exerciseLabel,
}: {
  disruptionUrn: string;
  hotelUrn: string;
  hotelName: string;
  exerciseLabel?: string | undefined;
}) {
  const { t } = useTranslation('disruptions');
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="outline" size="sm" className="h-7 gap-1 px-2 text-xs" onClick={() => setOpen(true)} data-testid="rooming-list-button">
        <FileText className="h-3.5 w-3.5" aria-hidden />
        {t('documents.rooming.button')}
      </Button>
      {open ? <RoomingListDialog disruptionUrn={disruptionUrn} hotelUrn={hotelUrn} hotelName={hotelName} exerciseLabel={exerciseLabel} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

export function RoomingListDialog({
  disruptionUrn,
  hotelUrn,
  hotelName,
  exerciseLabel,
  onClose,
}: {
  disruptionUrn: string;
  hotelUrn: string;
  hotelName: string;
  exerciseLabel?: string | undefined;
  onClose: () => void;
}) {
  const { t } = useTranslation('disruptions');
  const query = useRoomingList(disruptionUrn, hotelUrn);
  const download = useRoomingListDownload(disruptionUrn, hotelUrn);
  return (
    <DocumentSheetDialog
      open
      onClose={onClose}
      title={t('documents.rooming.dialogTitle', { hotel: query.data?.hotel.name ?? hotelName })}
      description={t('documents.rooming.dialogDescription')}
      ready={query.isSuccess}
      error={query.isError ? t('documents.error') : null}
      onDownloadCsv={() => download.mutate()}
      downloading={download.isPending}
      testId="rooming-list-dialog"
    >
      {query.data ? <RoomingListSheet list={query.data} exerciseLabel={exerciseLabel} /> : null}
    </DocumentSheetDialog>
  );
}
